import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleFreshFishVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "fresh-fish-online-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Top Online Retailers & Marketplaces",
    "section-2": "Local Regulations & Best Practices",
    "section-3": "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://ocean.org",
      label: "Ocean Wise Seafood Program",
    },
    {
      url: "https://organicocean.com",
      label: "Organic Ocean",
    },
    {
      url: "https://www2.gov.bc.ca/gov/content/industry/agriculture-seafood/fisheries-and-aquaculture",
      label: "BC Government – Fisheries and Aquaculture",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // For each main physical location (stores, fish markets, etc.),
  // we create an entry in locations and link them within Section 1
  // NOTE: The conversation mentioned:
  //  - A Fish Company: Lat: 49.10890, Lng: -122.82190
  //  - Fresh Ideas Start Here (Burnaby): Lat: 49.19785, Lng: -122.98870
  //  - The Daily Catch (Commercial Dr): Lat: 49.27300, Lng: -123.06900
  //  - The Daily Catch (English Bay): Lat: 49.28750, Lng: -123.13920
  //  - Seven Seas (Kitsilano): Lat: 49.26860, Lng: -123.15800
  //  - T&T (Chinatown): Lat: 49.27980, Lng: -123.10950
  //  - Save-On (Cambie & 7th): Lat: 49.26495, Lng: -123.11490
  //  - Organic Ocean (Office): Lat: 49.26485, Lng: -123.17030
  //  - Skipper Otto (Office): Lat: 49.26870, Lng: -123.14800

  const locations = [
    {
      id: "afishcompany",
      lat: 49.10890,
      lng: -122.82190,
      popupText: "A Fish Company – Surrey, BC",
    },
    {
      id: "freshideas",
      lat: 49.19785,
      lng: -122.98870,
      popupText: "Fresh Ideas Start Here (Burnaby)",
    },
    {
      id: "dailycatch-commercial",
      lat: 49.27300,
      lng: -123.06900,
      popupText: "The Daily Catch – Commercial Drive",
    },
    {
      id: "dailycatch-englishbay",
      lat: 49.28750,
      lng: -123.13920,
      popupText: "The Daily Catch – English Bay",
    },
    {
      id: "sevenseas",
      lat: 49.26860,
      lng: -123.15800,
      popupText: "Seven Seas Fish Market – Kitsilano",
    },
    {
      id: "tandt-chinatown",
      lat: 49.27980,
      lng: -123.10950,
      popupText: "T&T Supermarket – Chinatown",
    },
    {
      id: "saveon-cambie",
      lat: 49.26495,
      lng: -123.11490,
      popupText: "Save-On-Foods – Cambie & 7th",
    },
    {
      id: "organicocean",
      lat: 49.26485,
      lng: -123.17030,
      popupText: "Organic Ocean – Head Office",
    },
    {
      id: "skipperotto",
      lat: 49.26870,
      lng: -123.14800,
      popupText: "Skipper Otto – Office Location",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Fresh Fish to Order Online in Vancouver (Top Services & Tips)</title>
        <meta
          name="description"
          content="A thorough guide on ordering fresh fish online in Vancouver, with details on top retailers, local regulations, and sustainability tips."
        />
        <meta
          name="keywords"
          content="fresh fish, online ordering, Vancouver, seafood, sustainable"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/fresh-fish-online-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Fresh Fish to Order Online in Vancouver (Top Services & Tips)"
        />
        <meta
          property="og:description"
          content="A comprehensive overview of the best places to order fresh fish online in Vancouver, including sustainability guidelines, local regulations, FAQ, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/fresh-fish-online-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/fresh-fish-online-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Fresh Fish to Order Online in Vancouver (Top Services & Tips)"
        />
        <meta
          name="twitter:description"
          content="Discover top fish markets and retailers for ordering fresh seafood in Vancouver, plus local rules and best practices."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/fresh-fish-online-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left labelsMap={labelsMap} locations={locations} mapRef={mapRef} spotRefs={spotRefs} />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Variety of fresh fish on ice."
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              If you live in Vancouver and appreciate high-quality, sustainably
              sourced seafood, you’ll be pleased to learn that numerous retailers
              and markets deliver fresh fish right to your doorstep. From
              specialized fishmongers offering Ocean Wise-certified catch to
              large supermarkets with convenient online ordering, Vancouver
              provides a range of trustworthy options. This guide delves into
              the top places to order fresh fish online, shares best practices
              for handling and sustainability, and clarifies local regulations.
            </p>

            {/* SECTION: Top Online Retailers & Marketplaces */}
            <h2 id="section-1">Top Online Retailers & Marketplaces</h2>
            <p>
              Below is a list of reputable Vancouver-based or Vancouver-serving
              businesses providing fresh fish online. We’ve highlighted their
              sustainability credentials, delivery practices, and approximate price
              points. Whether you’re looking for a convenient grocery add-on or a
              curated box of premium seafood, these are some of the best options
              for fresh fish in the city.
            </p>

            {/* A Fish Company */}
            <p
              ref={(el) => setSpotRef("afishcompany", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>A Fish Company</strong>{" "}
              <Marker
                coordY={49.10890}
                coordX={-122.82190}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Located in Surrey, this boutique retailer offers wild and sustainably
              sourced Canadian fish. They deliver throughout Metro Vancouver, often with
              free delivery for orders above a certain threshold. Known for flash-frozen
              products like salmon, halibut, and sablefish, they emphasize Ocean Wise
              recommendations for responsible fishing.
            </p>

            {/* Fresh Ideas Start Here */}
            <p
              ref={(el) => setSpotRef("freshideas", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Fresh Ideas Start Here (F.I.S.H.)</strong>{" "}
              <Marker
                coordY={49.19785}
                coordX={-122.98870}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – With a storefront in Burnaby, F.I.S.H. stands out for its commitment to
              providing local, Ocean Wise-certified options. They carry seasonal West
              Coast specialties like spot prawns and halibut, along with gourmet
              prepared items like poké kits. Their online service offers home delivery in
              Metro Vancouver, free for orders above a certain amount.
            </p>

            {/* The Daily Catch (Commercial Drive and English Bay) */}
            <p
              ref={(el) => setSpotRef("dailycatch-commercial", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Daily Catch – Commercial Drive</strong>{" "}
              <Marker
                coordY={49.27300}
                coordX={-123.06900}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – This long-standing market prides itself on 100% Ocean Wise-approved
              seafood. They offer fresh fillets, smoked products, and shellfish online.
            </p>
            <p
              ref={(el) => setSpotRef("dailycatch-englishbay", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Daily Catch – English Bay</strong>{" "}
              <Marker
                coordY={49.28750}
                coordX={-123.13920}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A second location near English Bay, offering the same
              commitment to sustainability. Both outposts deliver in Vancouver with
              minimum order thresholds that vary by area.
            </p>

            {/* Seven Seas Fish Market (Kits) */}
            <p
              ref={(el) => setSpotRef("sevenseas", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Seven Seas Fish Market (Kitsilano)</strong>{" "}
              <Marker
                coordY={49.26860}
                coordX={-123.15800}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – A family-owned business operating for over half a century, Seven Seas
              in Kits is known for its wide range of locally sourced fish, shellfish,
              and even caviar. They champion sustainable, Ocean Wise options and
              provide online ordering with next-day local delivery.
            </p>

            {/* T&T Supermarket */}
            <p
              ref={(el) => setSpotRef("tandt-chinatown", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>T&T Supermarket (Chinatown)</strong>{" "}
              <Marker
                coordY={49.27980}
                coordX={-123.10950}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – A major Asian grocery chain, T&T offers fresh and often live seafood for
              online orders. Look for Ocean Wise or MSC labels among their stock.
              Delivery fees may vary, but they often waive them for larger orders.
            </p>

            {/* Save-On-Foods */}
            <p
              ref={(el) => setSpotRef("saveon-cambie", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Save-On-Foods (Cambie & 7th)</strong>{" "}
              <Marker
                coordY={49.26495}
                coordX={-123.11490}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – A mainstream grocery chain that labels many of its seafood items with the
              Ocean Wise symbol. Their online portal is easy to use, and they deliver
              throughout Vancouver with a modest minimum order requirement.
            </p>

            {/* Organic Ocean */}
            <p
              ref={(el) => setSpotRef("organicocean", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Organic Ocean (Head Office)</strong>{" "}
              <Marker
                coordY={49.26485}
                coordX={-123.17030}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Formerly a distributor, Organic Ocean now sells curated boxes of premium,
              flash-frozen seafood direct to consumers, all sustainably harvested and
              packaged in eco-friendly containers. They ship across Metro Vancouver with
              free shipping for larger orders.
            </p>

            {/* Skipper Otto (CSF Program) */}
            <p
              ref={(el) => setSpotRef("skipperotto", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Skipper Otto (Office Location)</strong>{" "}
              <Marker
                coordY={49.26870}
                coordX={-123.14800}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – A community-supported fishery (CSF) connecting local fishers with
              consumers. Members buy into a share at the start of the season and then
              pick their preferred items (wild salmon, spot prawns, etc.). They arrange
              pickup or occasional home delivery.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="section-2">Local Regulations & Best Practices</h2>
            <p>
              Vancouver’s seafood scene is governed by strict rules ensuring quality,
              safety, and sustainability:
            </p>
            <ul>
              <li>
                <strong>Food Safety:</strong> All vendors must keep fish chilled at or
                below 4 °C. Always refrigerate or freeze your delivery promptly and
                consume fresh fish within ~2 days.
              </li>
              <li>
                <strong>Sustainable Sourcing:</strong> Many local businesses partner
                with the Vancouver Aquarium’s Ocean Wise program, indicating their
                offerings are ethically sourced with minimal environmental impact.
              </li>
              <li>
                <strong>Legal Requirements:</strong> Businesses must hold appropriate
                licenses, and only legally caught and processed fish may be sold to the
                public. Recreationally caught fish cannot be sold commercially.
              </li>
              <li>
                <strong>Seasonality & Quotas:</strong> Some species (e.g., spot prawns,
                wild salmon) are only available certain times of the year, in line with
                local fisheries’ sustainable quotas.
              </li>
            </ul>
            <p>
              By verifying certification labels (e.g., Ocean Wise, MSC) and supporting
              reputable retailers, you help preserve marine ecosystems for future
              generations.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="section-3">FAQ</h2>
            <div className="faq-section">
              <h3>1. How is fish kept fresh during delivery?</h3>
              <p>
                Most reputable companies use insulated packaging with ice packs or dry
                ice to maintain proper temperatures. Upon arrival, place the fish in the
                fridge or freezer immediately.
              </p>

              <h3>2. Is there a minimum order amount for delivery?</h3>
              <p>
                Yes, minimums vary. Many shops offer free delivery when you spend above a
                certain threshold (e.g., $150). Others allow smaller orders but charge a
                delivery fee.
              </p>

              <h3>3. Are these options sustainably sourced?</h3>
              <p>
                In Vancouver, numerous providers participate in the Ocean Wise program,
                MSC certification, or other eco-labels. Check each company’s product
                descriptions to ensure the fish aligns with your sustainability values.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Ordering fresh fish online in Vancouver has never been easier, with various
              reputable retailers adhering to safety and sustainability principles.
              Whether you opt for premium local salmon from a boutique fishmonger or a
              broader selection from a major grocery chain, there’s an option that fits
              your preferences and budget. By following basic food safety guidelines and
              choosing sustainably sourced offerings, you can enjoy delicious,
              ocean-friendly seafood at home.
            </p>
            <p>
              <em>
                Disclaimer: Always check official resources for up-to-date
                safety regulations, shellfish advisories, and seasonal
                availability. If you suspect any issue with your order, contact
                the seller or relevant health authority.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}